<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'

// SolandaUI CSS tokens (base) + ALL theme palettes.
// `@federa/solanda-ui/css` only loads the default solanda theme; we also need
// the other presets so that ThemeManager.setPreset() actually changes the look.
import '@federa/solanda-ui/css'
import '@federa/solanda-ui/css/themes'
import { theme as themeManager } from '../theme'
import { showToast } from '@federa/solanda-ui/sl-toast'

// Components used in the home page
import '@federa/solanda-ui/sl-topbar'
import '@federa/solanda-ui/sl-button'
import '@federa/solanda-ui/sl-button-group'
import '@federa/solanda-ui/sl-tooltip'
import '@federa/solanda-ui/sl-badge'
import '@federa/solanda-ui/sl-section-header'
import '@federa/solanda-ui/sl-tabs'
import '@federa/solanda-ui/sl-tab'
import '@federa/solanda-ui/sl-tab-content'
import '@federa/solanda-ui/sl-step-indicator'
import '@federa/solanda-ui/sl-avatar'
import '@federa/solanda-ui/sl-switch'
import '@federa/solanda-ui/sl-card'
import '@federa/solanda-ui/sl-input'
import '@federa/solanda-ui/sl-sidebar'
import '@federa/solanda-ui/sl-list-group'
import '@federa/solanda-ui/sl-breadcrumb'
import '@federa/solanda-ui/sl-alert'
import '@federa/solanda-ui/sl-dropdown-menu'
import '@federa/solanda-ui/sl-accordion'
import '@federa/solanda-ui/sl-pagination'
import '@federa/solanda-ui/sl-skeleton'
import '@federa/solanda-ui/sl-label'
import '@federa/solanda-ui/sl-checkbox'
import '@federa/solanda-ui/sl-input-group'
import '@federa/solanda-ui/sl-select'
import '@federa/solanda-ui/sl-textarea'
import '@federa/solanda-ui/sl-offcanvas'
import '@federa/solanda-ui/sl-dialog'
import '@federa/solanda-ui/sl-footer'
import '@federa/solanda-ui/sl-spinner'

const goTo = (route: string) => {
  // MPA: navigate to a real page (home = '/', docs = '/docs/')
  window.location.href = route === 'docs' ? '/docs/' : '/'
}

// Open an external URL in a new tab (used by the GitLab / npm buttons).
// `window.open` can't be called directly in the template because `window` is
// not in the component's template scope under <script setup>.
const openExternal = (url: string) => window.open(url, '_blank', 'noopener')

const GITLAB_URL = 'https://gitlab.com/federa-social/libraries/solanda-ui'
const NPM_URL = 'https://www.npmjs.com/package/@federa/solanda-ui'

const installCmd = 'npm install @federa/solanda-ui'
const importSnippet = "import '@federa/solanda-ui/css'\nimport '@federa/solanda-ui'"
const appOffcanvas = ref<HTMLElement | null>(null)
const appDialog = ref<HTMLElement | null>(null)
const helloDialog = ref<HTMLElement | null>(null)

const showOffcanvas = () => (appOffcanvas.value as any)?.show()
const showDialog = () => (appDialog.value as any)?.showModal()
const closeDialog = () => (appDialog.value as any)?.close()
const showHello = () => (helloDialog.value as any)?.showModal()
const closeHello = () => (helloDialog.value as any)?.close()
const hideOffcanvas = (e: Event) => (e.target as HTMLElement).closest('sl-offcanvas')?.hide()

const PRESETS = [
  { id: 'solanda', name: 'Solanda', desc: 'Terracotta and Andean concrete. The default theme.' },
  { id: 'social-neutral', name: 'Social Neutral', desc: 'Sober and neutral, for serious products.' },
  { id: 'catppuccin', name: 'Catppuccin', desc: 'Soft pastel, loved by the community.' },
  { id: 'gruvbox', name: 'Gruvbox', desc: 'Warm retro terminal inspiration.' },
  { id: 'tokyo-night', name: 'Tokyo Night', desc: 'High-contrast neon night.' },
  { id: 'nord', name: 'Nord', desc: 'Arctic, bluish, and serene.' }
]

const modeLabel = ref('Sun Light mode')

const swatchHtml = (preset: string) => {
  const probe = document.createElement('div')
  probe.style.display = 'none'
  probe.className = `theme-${preset}${themeManager.getResolvedTheme() === 'dark' ? ' dark' : ''}`
  document.body.appendChild(probe)
  const cs = getComputedStyle(probe)
  const html = ['background', 'card', 'primary', 'secondary', 'accent', 'foreground']
    .map(v => `<span class="swatch" style="background:hsl(${cs.getPropertyValue(`--sl-hsl-${v}`).trim()})"></span>`)
    .join('')
  probe.remove()
  return html
}

const renderThemeUI = () => {
  const current = themeManager.getPreset()
  const mode = themeManager.getResolvedTheme()
  const dark = mode === 'dark'
  modeLabel.value = dark ? '☀ Light mode' : '☾ Dark mode'

  // The topbar toggle button reflects the *target* mode after clicking.
  // Use a short label on narrow screens so the topbar never overflows.
  const toggle = document.getElementById('theme-toggle')
  if (toggle) {
    const narrow = window.matchMedia('(max-width: 480px)').matches
    toggle.textContent = narrow ? (dark ? '☀' : '☾') : (dark ? '☀ Light mode' : '☾ Dark mode')
  }

  const modeGroup = document.getElementById('mode-group')
  if (modeGroup) modeGroup.setAttribute('value', mode)

  const cards = document.getElementById('theme-cards')
  if (cards) {
    cards.innerHTML = PRESETS.map(p => `
      <button class="theme-card" data-preset="${p.id}" aria-pressed="${p.id === current}">
        <span class="theme-card-name">${p.name}${p.id === current ? ' ✓' : ''}</span>
        <span class="theme-card-desc">${p.desc}</span>
        <span class="swatch-row">${swatchHtml(p.id)}</span>
      </button>
    `).join('')
  }

  const bento = document.getElementById('bento-themes')
  if (bento) {
    bento.innerHTML = PRESETS.map(p => `
      <button class="bento-theme-btn" data-preset="${p.id}" aria-pressed="${p.id === current}">
        <span>${p.name}</span>
        <span class="swatch-row">${swatchHtml(p.id)}</span>
      </button>
    `).join('')
  }
}

const toggleMode = () => {
  themeManager.toggleTheme()
  renderThemeUI()
}

const appToast = (variant: string, message: string) => {
  showToast({ message, variant: variant as any, duration: 3000 })
}

const copyText = (text: string) => {
  navigator.clipboard.writeText(text)
  showToast({ message: 'Copied to clipboard', variant: 'success', duration: 2000 })
}

const confirmDelete = () => {
  const dlg = document.getElementById('app-dialog') as any
  if (dlg) dlg.close()
  showToast({ message: "Relax: it's a demo, nothing was deleted 😅", variant: 'info', duration: 3500 })
}

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

const ELEMENTS = [
  'sl-button', 'sl-button-group', 'sl-input', 'sl-input-group', 'sl-input-group-addon',
  'sl-textarea', 'sl-select', 'sl-checkbox', 'sl-switch', 'sl-label', 'sl-badge',
  'sl-avatar', 'sl-avatar-image', 'sl-avatar-fallback', 'sl-card', 'sl-icon',
  'sl-alert', 'sl-spinner', 'sl-skeleton', 'sl-tabs', 'sl-tab', 'sl-tab-content',
  'sl-breadcrumb', 'sl-breadcrumb-item', 'sl-pagination', 'sl-dialog', 'sl-tooltip',
  'sl-popover', 'sl-popover-trigger', 'sl-popover-content', 'sl-dropdown-menu',
  'sl-dropdown-menu-trigger', 'sl-dropdown-menu-content', 'sl-dropdown-menu-item',
  'sl-dropdown-menu-separator', 'sl-toast', 'sl-offcanvas', 'sl-step-indicator',
  'sl-accordion', 'sl-accordion-item', 'sl-list-group', 'sl-list-group-item',
  'sl-section-header', 'sl-feature-card', 'sl-showcase-card', 'sl-doc-block',
  'sl-sidebar', 'sl-topbar', 'sl-footer'
]

const fillMarquee = (id: string, items: string[]) => {
  const el = document.getElementById(id)
  if (!el) return
  const chips = items.map(n => `<span class="marquee-chip">&lt;${n}&gt;</span>`).join('')
  el.innerHTML = chips + chips
}

let statsObserver: IntersectionObserver | null = null
let revealObserver: IntersectionObserver | null = null
let spyObserver: IntersectionObserver | null = null

const countUp = (el: HTMLElement) => {
  const target = parseInt(el.dataset.count || '0', 10)
  const prefix = el.dataset.prefix || ''
  const suffix = el.dataset.suffix || ''
  const duration = 1300
  const t0 = performance.now()
  const tick = (t: number) => {
    const p = Math.min((t - t0) / duration, 1)
    const eased = 1 - Math.pow(1 - p, 3)
    el.textContent = prefix + Math.round(target * eased) + suffix
    if (p < 1) requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

const onDocClick = (e: MouseEvent) => {
  const btn = (e.target as HTMLElement).closest('[data-preset]') as HTMLElement | null
  if (!btn) return
  const preset = btn.dataset.preset || ''
  themeManager.setPreset(preset)
  renderThemeUI()
  const p = PRESETS.find(x => x.id === preset)
  showToast({ message: `Theme "${p?.name}" applied`, variant: 'success', duration: 2000 })
}

const onModeGroupChange = (e: Event) => {
  const detail = (e as CustomEvent).detail
  themeManager.setTheme(detail.value)
  renderThemeUI()
}

const onLoadingSwitch = (e: Event) => {
  const detail = (e as CustomEvent).detail
  const loading = detail.checked
  const content = document.getElementById('activity-content')
  const skeleton = document.getElementById('activity-skeleton')
  if (content) content.hidden = loading
  if (skeleton) skeleton.hidden = !loading
}

const onUserMenu = (e: Event) => {
  const detail = (e as CustomEvent).detail
  appToast('info', `Menu action: ${detail.value}`)
}
const onProjectMenu = (e: Event) => {
  const detail = (e as CustomEvent).detail
  appToast('info', `Project action: ${detail.value}`)
}
const onDemoPagination = (e: Event) => {
  const detail = (e as CustomEvent).detail
  appToast('info', `Page ${detail.page ?? detail.current ?? detail.value ?? ''} requested`)
}

onMounted(() => {
  renderThemeUI()

  const modeGroup = document.getElementById('mode-group')
  modeGroup?.addEventListener('sl-buttongroup-change', onModeGroupChange)
  document.addEventListener('click', onDocClick)

  const loadingSwitch = document.getElementById('loading-switch')
  loadingSwitch?.addEventListener('change', onLoadingSwitch)

  document.getElementById('app-user-menu')?.addEventListener('sl-item-select', onUserMenu)
  document.getElementById('app-project-menu')?.addEventListener('sl-item-select', onProjectMenu)
  document.querySelector('#demo sl-pagination')?.addEventListener('sl-page-change', onDemoPagination)

  const half = Math.ceil(ELEMENTS.length / 2)
  fillMarquee('marquee-1', ELEMENTS.slice(0, half))
  fillMarquee('marquee-2', ELEMENTS.slice(half))

  statsObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (!e.isIntersecting) return
      e.target.querySelectorAll<HTMLElement>('[data-count]').forEach(countUp)
      statsObserver?.unobserve(e.target)
    })
  }, { threshold: 0.4 })
  document.querySelectorAll('.hero-stats').forEach(el => statsObserver?.observe(el))

  revealObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('is-visible')
        revealObserver?.unobserve(e.target)
      }
    })
  }, { threshold: 0.12 })
  document.querySelectorAll('.reveal').forEach(el => revealObserver?.observe(el))

  const navLinks = [...document.querySelectorAll<HTMLElement>('#nav-links a[href^="#"]')]
  spyObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (!e.isIntersecting) return
      const id = (e.target as HTMLElement).id
      navLinks.forEach(a => a.classList.toggle('active', a.getAttribute('href') === `#${id}`))
    })
  }, { rootMargin: '-30% 0px -60% 0px' })
  document.querySelectorAll('section[id]').forEach(s => spyObserver?.observe(s))

  // Keep the topbar toggle label in sync with the viewport width
  window.addEventListener('resize', renderThemeUI, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('resize', renderThemeUI)
  document.getElementById('mode-group')?.removeEventListener('sl-buttongroup-change', onModeGroupChange)
  document.removeEventListener('click', onDocClick)
  document.getElementById('loading-switch')?.removeEventListener('change', onLoadingSwitch)
  document.getElementById('app-user-menu')?.removeEventListener('sl-item-select', onUserMenu)
  document.getElementById('app-project-menu')?.removeEventListener('sl-item-select', onProjectMenu)
  document.querySelector('#demo sl-pagination')?.removeEventListener('sl-page-change', onDemoPagination)
  statsObserver?.disconnect()
  revealObserver?.disconnect()
  spyObserver?.disconnect()
})
</script>

<template>
  <svg width="0" height="0" style="position:absolute" aria-hidden="true" focusable="false">
    <defs>
      <pattern id="logo-stripes" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="8" height="8" style="fill:var(--logo-muted)"/>
        <rect width="4" height="8" style="fill:var(--logo-ink)"/>
      </pattern>
      <pattern id="logo-dots" width="10" height="10" patternUnits="userSpaceOnUse">
        <rect width="10" height="10" style="fill:var(--logo-soft)"/>
        <circle cx="5" cy="5" r="1.8" style="fill:var(--logo-ink)"/>
      </pattern>

      <!-- Mark: Solanda block — window, cursor, checkbox, toggle (viewBox 0 0 120 120) -->
      <g id="solanda-mark">
        <rect x="12" y="12" width="104" height="104" rx="18" style="fill:var(--logo-ink)"/>
        <rect x="4" y="4" width="104" height="104" rx="18" style="fill:var(--logo-paper);stroke:var(--logo-ink)" stroke-width="5"/>
        <rect x="18" y="18" width="34" height="34" rx="5" style="fill:var(--logo-paper);stroke:var(--logo-ink)" stroke-width="3.5"/>
        <path d="M 23 18 H 47 A 5 5 0 0 1 52 23 V 28 H 18 V 23 A 5 5 0 0 1 23 18 Z" style="fill:var(--logo-ink)"/>
        <circle cx="46.5" cy="23" r="1.8" style="fill:var(--logo-paper)"/>
        <rect x="24" y="34" width="22" height="4" rx="2" style="fill:var(--logo-half)"/>
        <rect x="24" y="42" width="14" height="4" rx="2" style="fill:var(--logo-half)"/>
        <rect x="60" y="18" width="34" height="34" rx="5" style="fill:url(#logo-stripes);stroke:var(--logo-ink)" stroke-width="3.5"/>
        <path transform="translate(70,22) scale(1.4)" stroke-linejoin="round"
              d="M 0 0 L 0 17 L 4.6 13.2 L 7.6 19.6 L 10.8 18 L 7.8 11.8 L 13.2 11.8 Z"
              style="fill:var(--logo-paper);stroke:var(--logo-ink)" stroke-width="2.2"/>
        <rect x="18" y="60" width="34" height="34" rx="5" style="fill:url(#logo-dots);stroke:var(--logo-ink)" stroke-width="3.5"/>
        <rect x="26" y="68" width="18" height="18" rx="3.5" style="fill:var(--logo-paper);stroke:var(--logo-ink)" stroke-width="3"/>
        <path d="M 30.5 77 L 34.5 81.5 L 42.5 72.5" fill="none" style="stroke:var(--logo-ink)" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>
        <rect x="60" y="60" width="34" height="34" rx="5" style="fill:var(--logo-accent);stroke:var(--logo-ink)" stroke-width="3.5"/>
        <rect x="64" y="70" width="26" height="14" rx="7" style="fill:var(--logo-paper);stroke:var(--logo-ink)" stroke-width="3"/>
        <circle cx="83" cy="77" r="4.5" style="fill:var(--logo-ink)"/>
      </g>

      <!-- Full logo: mark + pixel wordmark + UI badge (viewBox 0 0 612 140) -->
      <g id="solanda-logo-full">
        <use href="#solanda-mark" transform="translate(10,10)"/>
        <path style="fill:var(--logo-ink)" d="M158 42h24v8h-24zM150 50h8v8h-8zM182 50h8v8h-8zM150 58h8v8h-8zM158 66h24v8h-24zM182 74h8v8h-8zM150 82h8v8h-8zM182 82h8v8h-8zM158 90h24v8h-24zM206 42h24v8h-24zM198 50h8v8h-8zM230 50h8v8h-8zM198 58h8v8h-8zM230 58h8v8h-8zM198 66h8v8h-8zM230 66h8v8h-8zM198 74h8v8h-8zM230 74h8v8h-8zM198 82h8v8h-8zM230 82h8v8h-8zM206 90h24v8h-24zM246 42h8v8h-8zM246 50h8v8h-8zM246 58h8v8h-8zM246 66h8v8h-8zM246 74h8v8h-8zM246 82h8v8h-8zM246 90h40v8h-40zM302 42h24v8h-24zM294 50h8v8h-8zM326 50h8v8h-8zM294 58h8v8h-8zM326 58h8v8h-8zM294 66h40v8h-40zM294 74h8v8h-8zM326 74h8v8h-8zM294 82h8v8h-8zM326 82h8v8h-8zM294 90h8v8h-8zM326 90h8v8h-8zM342 42h8v8h-8zM374 42h8v8h-8zM342 50h16v8h-16zM374 50h8v8h-8zM342 58h8v8h-8zM358 58h8v8h-8zM374 58h8v8h-8zM342 66h8v8h-8zM366 66h16v8h-16zM342 74h8v8h-8zM374 74h8v8h-8zM342 82h8v8h-8zM374 82h8v8h-8zM342 90h8v8h-8zM374 90h8v8h-8zM390 42h32v8h-32zM390 50h8v8h-8zM422 50h8v8h-8zM390 58h8v8h-8zM422 58h8v8h-8zM390 66h8v8h-8zM422 66h8v8h-8zM390 74h8v8h-8zM422 74h8v8h-8zM390 82h8v8h-8zM422 82h8v8h-8zM390 90h32v8h-32zM446 42h24v8h-24zM438 50h8v8h-8zM470 50h8v8h-8zM438 58h8v8h-8zM470 58h8v8h-8zM438 66h40v8h-40zM438 74h8v8h-8zM470 74h8v8h-8zM438 82h8v8h-8zM470 82h8v8h-8zM438 90h8v8h-8zM470 90h8v8h-8z"/>
        <rect x="500" y="43" width="94" height="66" rx="12" style="fill:var(--logo-half)"/>
        <rect x="494" y="37" width="94" height="66" rx="12" style="fill:var(--logo-ink)"/>
        <path style="fill:var(--logo-paper)" d="M508 49h6v6h-6zM532 49h6v6h-6zM508 55h6v6h-6zM532 55h6v6h-6zM508 61h6v6h-6zM532 61h6v6h-6zM508 67h6v6h-6zM532 67h6v6h-6zM508 73h6v6h-6zM532 73h6v6h-6zM508 79h6v6h-6zM532 79h6v6h-6zM514 85h18v6h-18zM544 49h30v6h-30zM556 55h6v6h-6zM556 61h6v6h-6zM556 67h6v6h-6zM556 73h6v6h-6zM556 79h6v6h-6zM544 85h30v6h-30z"/>
      </g>
    </defs>
  </svg>

  <!-- Nav (sl-topbar) -->
  <sl-topbar variant="pinned" class="landing-nav">
    <a slot="start" href="/" class="brand-link">
      <svg class="brand-mark" viewBox="0 0 120 120" aria-hidden="true" focusable="false"><use href="#solanda-mark"/></svg>
      SolandaUI
    </a>
    <nav slot="center" class="topbar-links" id="nav-links">
      <a href="#features">Features</a>
      <a href="#demo">Demo</a>
      <a href="#themes">Themes</a>
      <a href="#get-started">Get Started</a>
      <a href="/docs/">Documentation</a>
    </nav>
    <div slot="end" class="topbar-actions">
      <div class="lang-selector" style="display:flex;align-items:center;gap:0.25rem;margin-right:0.5rem;">
        <span style="font-size:var(--sl-font-size-xs);color:hsl(var(--sl-hsl-foreground));font-weight:var(--sl-font-weight-semibold);">English</span>
        <span style="color:hsl(var(--sl-hsl-border));">|</span>
        <a href="/es/" hreflang="es" style="font-size:var(--sl-font-size-xs);color:hsl(var(--sl-hsl-muted-foreground));text-decoration:none;padding:0.25rem 0.5rem;border-radius:var(--sl-radius-md);transition:color 0.15s,background-color 0.15s;" onmouseover="this.style.color='hsl(var(--sl-hsl-foreground))';this.style.background='hsl(var(--sl-hsl-muted))';" onmouseout="this.style.color='';this.style.background='';">Español</a>
      </div>
      <sl-tooltip content="Toggle between light and dark mode" placement="bottom">
        <sl-button size="sm" variant="outline" id="theme-toggle" @click="toggleMode">Sun Light mode</sl-button>
      </sl-tooltip>
      <sl-button size="sm" variant="ghost" @click="openExternal(GITLAB_URL)">GitLab</sl-button>
    </div>
  </sl-topbar>

  <!-- Hero -->
  <header class="hero">
    <div class="container-wide">
      <div class="hero-copy">
        <svg class="hero-logo" viewBox="0 0 612 140" role="img" aria-label="SolandaUI"><use href="#solanda-logo-full"/></svg>
        <div class="hero-badge">
          <span class="hero-badge-dot"></span>
          v1.5.0 · Production-ready · 0 dependencies
        </div>
        <h1 class="hero-title">
          Components with<br>
          <em>their own identity</em>
        </h1>
        <p class="hero-subtitle">
          Like the neighborhood it's named after — designed in 1984, alive far beyond its borders — SolandaUI is a structured foundation that teams make their own. It works with Vue, React, Angular, and plain HTML, because it doesn't belong to any framework: it belongs to the platform.
        </p>
        <div class="hero-actions">
          <sl-button size="lg" @click="goTo('docs')">View documentation</sl-button>
          <sl-button size="lg" variant="outline" @click="scrollTo('demo')">Try the demo ↓</sl-button>
        </div>
        <div class="hero-install">
          <code>npm install @federa/solanda-ui</code>
          <sl-button size="xs" variant="ghost" @click="copyText(installCmd)" aria-label="Copy install command">⧉</sl-button>
        </div>
        <div class="hero-stats">
          <div class="hero-stat">
            <div class="hero-stat-value" data-count="52">0</div>
            <div class="hero-stat-label">Components</div>
          </div>
          <div class="hero-stat">
            <div class="hero-stat-value" data-count="65">0</div>
            <div class="hero-stat-label">Custom elements</div>
          </div>
          <div class="hero-stat">
            <div class="hero-stat-value" data-count="6">0</div>
            <div class="hero-stat-label">Themes included</div>
          </div>
          <div class="hero-stat">
            <div class="hero-stat-value" data-count="36" data-prefix="~" data-suffix=" KB">~0 KB</div>
            <div class="hero-stat-label">Full gzip bundle</div>
          </div>
        </div>
      </div>

      <!-- Floating app built with the library itself -->
      <div class="hero-visual" aria-hidden="true">
        <div class="hero-float hero-float-a">
          <sl-badge variant="success" size="sm">Deploy OK</sl-badge>
          <span class="text-muted">2 min ago</span>
        </div>
        <div class="hero-app">
          <div class="hero-app-header">
            <span class="hero-app-title">Deployment pipeline</span>
            <sl-badge variant="info">In progress</sl-badge>
          </div>
          <sl-step-indicator
            steps='[{"label":"Build"},{"label":"Test"},{"label":"Deploy"},{"label":"Live"}]'
            current="3"
            variant="horizontal"
          ></sl-step-indicator>
          <div class="hero-app-row">
            <div class="hero-app-row-label">
              Assigned reviewers
              <small>3 of 4 approvals</small>
            </div>
            <div class="avatar-stack">
              <sl-avatar size="sm"><sl-avatar-fallback>MA</sl-avatar-fallback></sl-avatar>
              <sl-avatar size="sm"><sl-avatar-fallback>JC</sl-avatar-fallback></sl-avatar>
              <sl-avatar size="sm"><sl-avatar-fallback>LP</sl-avatar-fallback></sl-avatar>
              <sl-avatar size="sm"><sl-avatar-fallback>+2</sl-avatar-fallback></sl-avatar>
            </div>
          </div>
          <div class="hero-app-row">
            <div class="hero-app-row-label">
              Auto deploy
              <small>main → production</small>
            </div>
            <sl-switch checked></sl-switch>
          </div>
          <div style="display:flex;gap:0.5rem;justify-content:flex-end">
            <sl-button size="sm" variant="ghost">View logs</sl-button>
            <sl-button size="sm">Promote to Live</sl-button>
          </div>
        </div>
        <div class="hero-float hero-float-b">
          <sl-spinner size="sm"></sl-spinner>
          <span>Build <strong>#214</strong> compiling...</span>
        </div>
      </div>
    </div>
  </header>

  <!-- Features bento -->
  <section class="features" id="features">
    <div class="container-wide">
      <sl-section-header eyebrow="Why SolandaUI">
        <span slot="title">Structure is the starting point</span>
        A clear plan that teams adopt, adapt, and take further than the original design envisioned.
      </sl-section-header>

      <div class="bento">

        <!-- Multi-framework (large cell) -->
        <article class="bento-cell bento-2 bento-tall reveal">
          <div class="bento-eyebrow">Framework-agnostic</div>
          <h3 class="bento-title">Write once. Use everywhere.</h3>
          <p class="bento-desc">
            Custom Elements v1 and Shadow DOM: the same component works in any stack without wrappers or adapters.
          </p>
          <sl-tabs active="fw-html">
            <sl-tab name="fw-html" size="sm" active>HTML</sl-tab>
            <sl-tab name="fw-vue" size="sm">Vue</sl-tab>
            <sl-tab name="fw-react" size="sm">React</sl-tab>
            <sl-tab name="fw-ng" size="sm">Angular</sl-tab>
          </sl-tabs>
          <sl-tab-content name="fw-html" active no-padding>
            <pre class="code-block" style="margin-top:0.75rem"><code><span class="c-com">&lt;!-- No prior steps: import and use --&gt;</span>
<span class="c-tag">&lt;script</span> <span class="c-attr">type</span>=<span class="c-str">"module"</span><span class="c-tag">&gt;</span>
  <span class="c-kw">import</span> <span class="c-str">'@federa/solanda-ui/sl-button'</span>
<span class="c-tag">&lt;/script&gt;</span>

<span class="c-tag">&lt;sl-button</span> <span class="c-attr">variant</span>=<span class="c-str">"default"</span> <span class="c-attr">size</span>=<span class="c-str">"lg"</span><span class="c-tag">&gt;</span>
  Save changes
<span class="c-tag">&lt;/sl-button&gt;</span></code></pre>
          </sl-tab-content>
          <sl-tab-content name="fw-vue" no-padding>
            <pre class="code-block" style="margin-top:0.75rem"><code><span class="c-tag">&lt;script</span> <span class="c-attr">setup</span><span class="c-tag">&gt;</span>
<span class="c-kw">import</span> <span class="c-str">'@federa/solanda-ui/sl-button'</span>
<span class="c-tag">&lt;/script&gt;</span>

<span class="c-tag">&lt;template&gt;</span>
  <span class="c-tag">&lt;sl-button</span> <span class="c-attr">@click</span>=<span class="c-str">"save"</span><span class="c-tag">&gt;</span>Save changes<span class="c-tag">&lt;/sl-button&gt;</span>
<span class="c-tag">&lt;/template&gt;</span></code></pre>
          </sl-tab-content>
          <sl-tab-content name="fw-react" no-padding>
            <pre class="code-block" style="margin-top:0.75rem"><code><span class="c-kw">import</span> <span class="c-str">'@federa/solanda-ui/sl-button'</span>

<span class="c-kw">export function</span> Actions() {
  <span class="c-kw">return</span> (
    <span class="c-tag">&lt;sl-button</span> <span class="c-attr">onClick</span>={save}<span class="c-tag">&gt;</span>Save changes<span class="c-tag">&lt;/sl-button&gt;</span>
  )
}</code></pre>
          </sl-tab-content>
          <sl-tab-content name="fw-ng" no-padding>
            <pre class="code-block" style="margin-top:0.75rem"><code><span class="c-com">// app.module.ts → schemas: [CUSTOM_ELEMENTS_SCHEMA]</span>
<span class="c-kw">import</span> <span class="c-str">'@federa/solanda-ui/sl-button'</span>

<span class="c-com">&lt;!-- template --&gt;</span>
<span class="c-tag">&lt;sl-button</span> <span class="c-attr">(click)</span>=<span class="c-str">"save()"</span><span class="c-tag">&gt;</span>Save changes<span class="c-tag">&lt;/sl-button&gt;</span></code></pre>
          </sl-tab-content>
        </article>

        <!-- Weight -->
        <article class="bento-cell bento-2 reveal">
          <div class="bento-eyebrow">Lightweight</div>
          <h3 class="bento-title">~36 KB gzip. All included.</h3>
          <p class="bento-desc">All 52 components with their embedded CSS. With tree-shaking, you only pay for what you use.</p>
          <div class="weight-bars">
            <div class="weight-row">
              <div class="weight-row-head"><strong>Full SolandaUI</strong><span>~36 KB</span></div>
              <div class="weight-track"><div class="weight-fill" style="--w:36%"></div></div>
            </div>
            <div class="weight-row">
              <div class="weight-row-head"><strong>Button + input + dialog only</strong><span>~7 KB</span></div>
              <div class="weight-track"><div class="weight-fill" style="--w:7%"></div></div>
            </div>
            <div class="weight-row">
              <div class="weight-row-head"><strong>Typical UI kit + framework runtime</strong><span>&gt;100 KB</span></div>
              <div class="weight-track"><div class="weight-fill weight-fill--muted" style="--w:100%"></div></div>
            </div>
          </div>
        </article>

        <!-- Themes -->
        <article class="bento-cell bento-tall reveal">
          <div class="bento-eyebrow">Themes</div>
          <h3 class="bento-title">6 identities out of the box</h3>
          <p class="bento-desc">Click one and see how this entire page changes.</p>
          <div class="bento-themes-list" id="bento-themes"></div>
        </article>

        <!-- Accessibility -->
        <article class="bento-cell reveal">
          <div class="bento-eyebrow">Accessibility</div>
          <h3 class="bento-title">Keyboard first</h3>
          <p class="bento-desc">
            ARIA roles, focus trap in overlays, and keyboard navigation in every component. Press <span class="token-chip"><b>Tab</b></span> and check it out.
          </p>
        </article>

        <!-- Tokens -->
        <article class="bento-cell reveal">
          <div class="bento-eyebrow">Design tokens</div>
          <h3 class="bento-title">Your brand, in variables</h3>
          <p class="bento-desc">The entire system is controlled with CSS custom properties:</p>
          <div class="token-chips">
            <span class="token-chip">--sl-hsl-<b>primary</b></span>
            <span class="token-chip">--sl-<b>radius</b>-lg</span>
            <span class="token-chip">--sl-<b>spacing</b>-4</span>
            <span class="token-chip">--sl-<b>shadow</b>-md</span>
            <span class="token-chip">--sl-<b>font</b>-family-base</span>
          </div>
        </article>

        <!-- Tooling -->
        <article class="bento-cell bento-2 reveal">
          <div class="bento-eyebrow">Developer experience</div>
          <h3 class="bento-title">TypeScript, manifest, and docs for humans and machines</h3>
          <p class="bento-desc">
            Complete types, <code class="token-chip">custom-elements.json</code> for editor autocompletion, and <code class="token-chip">llms.txt</code> generated on each build so your AI assistant knows the library as well as you do.
          </p>
        </article>

      </div>
    </div>
  </section>

  <!-- Live demo: simulated app -->
  <section id="demo">
    <div class="container-wide">
      <sl-section-header eyebrow="Live Demo">
        <span slot="title">It's not a catalog. It's an application.</span>
        Everything you see inside this window is built with SolandaUI: navigation, forms, loading states, overlays, and notifications. Touch everything.
      </sl-section-header>

      <div class="app-window reveal">
        <div class="app-window-bar">
          <div class="window-dot" style="background:#ff5f57"></div>
          <div class="window-dot" style="background:#febc2e"></div>
          <div class="window-dot" style="background:#28c840"></div>
          <div class="window-url">app.solanda.dev/projects/solanda</div>
        </div>

        <sl-topbar variant="flat">
          <strong slot="start" style="display:flex;align-items:center;gap:0.5rem">
            <span class="logo-dot"></span> Acme Cloud
          </strong>
          <sl-input slot="center" placeholder="Search projects, teams..." style="width:min(320px,100%)"></sl-input>
          <div slot="end" style="display:flex;align-items:center;gap:0.625rem">
            <sl-tooltip content="Notifications" placement="bottom">
              <sl-button size="sm" variant="ghost" @click="showOffcanvas" aria-label="Open notifications">🔔</sl-button>
            </sl-tooltip>
            <sl-dropdown-menu id="app-user-menu">
              <sl-dropdown-menu-trigger>
                <sl-avatar size="sm" style="cursor:pointer">
                  <sl-avatar-image src="https://i.pravatar.cc/150?img=12" alt="User account"></sl-avatar-image>
                  <sl-avatar-fallback>CC</sl-avatar-fallback>
                </sl-avatar>
              </sl-dropdown-menu-trigger>
              <sl-dropdown-menu-content>
                <sl-dropdown-menu-item value="profile" label="My profile"></sl-dropdown-menu-item>
                <sl-dropdown-menu-item value="prefs" label="Preferences"></sl-dropdown-menu-item>
                <sl-dropdown-menu-separator></sl-dropdown-menu-separator>
                <sl-dropdown-menu-item value="logout" label="Sign out"></sl-dropdown-menu-item>
              </sl-dropdown-menu-content>
            </sl-dropdown-menu>
          </div>
        </sl-topbar>

        <div class="app-body">
          <sl-sidebar variant="floating" width="208px">
            <div class="app-nav-section">Workspace</div>
            <sl-list-group variant="flush">
              <sl-list-group-item active>📊 Dashboard</sl-list-group-item>
              <sl-list-group-item>📦 Projects</sl-list-group-item>
              <sl-list-group-item>👥 Team</sl-list-group-item>
              <sl-list-group-item>🧾 Billing</sl-list-group-item>
              <sl-list-group-item disabled>🧪 Labs (coming soon)</sl-list-group-item>
            </sl-list-group>
            <div class="app-nav-section">Account</div>
            <sl-list-group variant="flush">
              <sl-list-group-item>⚙️ Settings</sl-list-group-item>
              <sl-list-group-item>❓ Help</sl-list-group-item>
            </sl-list-group>
          </sl-sidebar>

          <main class="app-main">
            <sl-breadcrumb>
              <sl-breadcrumb-item href="#demo">Acme Cloud</sl-breadcrumb-item>
              <sl-breadcrumb-item href="#demo">Projects</sl-breadcrumb-item>
              <sl-breadcrumb-item active>Solanda</sl-breadcrumb-item>
            </sl-breadcrumb>

            <div class="app-page-head">
              <h3 class="app-page-title">
                Solanda Project
                <sl-badge variant="success">In production</sl-badge>
              </h3>
              <div class="app-actions">
                <sl-dropdown-menu id="app-project-menu">
                  <sl-dropdown-menu-trigger>
                    <sl-button size="sm" variant="outline">Actions ▾</sl-button>
                  </sl-dropdown-menu-trigger>
                  <sl-dropdown-menu-content>
                    <sl-dropdown-menu-item value="dupe" label="Duplicate project"></sl-dropdown-menu-item>
                    <sl-dropdown-menu-item value="export" label="Export configuration"></sl-dropdown-menu-item>
                    <sl-dropdown-menu-separator></sl-dropdown-menu-separator>
                    <sl-dropdown-menu-item value="archive" label="Archive"></sl-dropdown-menu-item>
                  </sl-dropdown-menu-content>
                </sl-dropdown-menu>
                <sl-button size="sm" @click="appToast('success', 'Deployment started: build #215 queued')">Deploy</sl-button>
              </div>
            </div>

            <sl-alert variant="info" title="SolandaUI is here" dismissible>
              Production-ready UI library: 52 components, 65 custom elements, 6 themes, stable API, and zero dependencies.
            </sl-alert>

            <sl-tabs>
              <sl-tab name="app-summary" active>Summary</sl-tab>
              <sl-tab name="app-activity">Activity</sl-tab>
              <sl-tab name="app-settings">Settings</sl-tab>
            </sl-tabs>

            <!-- Tab: Summary -->
            <sl-tab-content name="app-summary" active>
              <div style="display:flex;flex-direction:column;gap:1.25rem">
                <div class="app-stats">
                  <sl-card>
                    <div class="app-stat-label">Requests / day</div>
                    <div class="app-stat-value">1.2 M</div>
                    <sl-badge variant="success" size="sm">▲ 12%</sl-badge>
                  </sl-card>
                  <sl-card>
                    <div class="app-stat-label">P95 latency</div>
                    <div class="app-stat-value">84 ms</div>
                    <sl-badge variant="info" size="sm">stable</sl-badge>
                  </sl-card>
                  <sl-card>
                    <div class="app-stat-label">5xx errors</div>
                    <div class="app-stat-value">0.02%</div>
                    <sl-badge variant="warning" size="sm">▲ 0.01</sl-badge>
                  </sl-card>
                </div>

                <sl-list-group>
                  <sl-list-group-item>
                    <div class="app-list-row">
                      <div class="app-list-main">
                        <sl-avatar size="sm"><sl-avatar-fallback>MA</sl-avatar-fallback></sl-avatar>
                        <div class="app-list-text">
                          <span>Deploy #214 — main</span>
                          <small>2 minutes ago · 1m 43s</small>
                        </div>
                      </div>
                      <sl-badge variant="success" size="sm">Completed</sl-badge>
                    </div>
                  </sl-list-group-item>
                  <sl-list-group-item>
                    <div class="app-list-row">
                      <div class="app-list-main">
                        <sl-avatar size="sm"><sl-avatar-fallback>JC</sl-avatar-fallback></sl-avatar>
                        <div class="app-list-text">
                          <span>Deploy #213 — feature/themes</span>
                          <small>1 hour ago · 2m 10s</small>
                        </div>
                      </div>
                      <sl-badge variant="error" size="sm">Failed</sl-badge>
                    </div>
                  </sl-list-group-item>
                  <sl-list-group-item>
                    <div class="app-list-row">
                      <div class="app-list-main">
                        <sl-avatar size="sm"><sl-avatar-fallback>LP</sl-avatar-fallback></sl-avatar>
                        <div class="app-list-text">
                          <span>Deploy #212 — main</span>
                          <small>yesterday · 1m 51s</small>
                        </div>
                      </div>
                      <sl-badge variant="success" size="sm">Completed</sl-badge>
                    </div>
                  </sl-list-group-item>
                </sl-list-group>

                <div class="demo-pagination-row">
                  <span class="text-muted" style="font-size:var(--sl-font-size-xs)">Showing 3 of 214 deployments</span>
                  <sl-pagination total="72" current="1" size="sm"></sl-pagination>
                </div>
              </div>
            </sl-tab-content>

            <!-- Tab: Activity -->
            <sl-tab-content name="app-activity">
              <div style="display:flex;flex-direction:column;gap:1.25rem">
                <div style="display:flex;align-items:center;gap:0.75rem">
                  <sl-switch id="loading-switch"></sl-switch>
                  <sl-label>Simulate loading state (skeleton)</sl-label>
                </div>

                <div id="activity-content">
                  <sl-accordion>
                    <sl-accordion-item open>
                      <span slot="title">Today</span>
                      <p style="margin:0 0 0.5rem">✅ <strong>Maria</strong> approved the merge request <em>feature/themes</em>.</p>
                      <p style="margin:0">🚀 <strong>CI</strong> deployed build #214 to production.</p>
                    </sl-accordion-item>
                    <sl-accordion-item>
                      <span slot="title">Yesterday</span>
                      <p style="margin:0">🐛 <strong>Luis</strong> reported a bug in the Gruvbox theme (resolved).</p>
                    </sl-accordion-item>
                    <sl-accordion-item>
                      <span slot="title">This week</span>
                      <p style="margin:0">📦 <strong>v1.5.0</strong> is live on npm: 52 components, 65 custom elements, zero dependencies. 🎉</p>
                    </sl-accordion-item>
                  </sl-accordion>
                </div>

                <div id="activity-skeleton" hidden>
                  <div style="display:flex;flex-direction:column;gap:1rem">
                    <div style="display:flex;gap:0.75rem;align-items:center">
                      <sl-skeleton variant="circle" width="40px" height="40px"></sl-skeleton>
                      <div style="flex:1;display:flex;flex-direction:column;gap:0.5rem">
                        <sl-skeleton variant="text" width="55%"></sl-skeleton>
                        <sl-skeleton variant="text" width="35%"></sl-skeleton>
                      </div>
                    </div>
                    <sl-skeleton variant="rectangle" width="100%" height="72px"></sl-skeleton>
                    <div style="display:flex;gap:0.75rem;align-items:center">
                      <sl-skeleton variant="circle" width="40px" height="40px"></sl-skeleton>
                      <div style="flex:1;display:flex;flex-direction:column;gap:0.5rem">
                        <sl-skeleton variant="text" width="70%"></sl-skeleton>
                        <sl-skeleton variant="text" width="45%"></sl-skeleton>
                      </div>
                    </div>
                    <sl-skeleton variant="rectangle" width="100%" height="72px"></sl-skeleton>
                  </div>
                </div>
              </div>
            </sl-tab-content>

            <!-- Tab: Settings -->
            <sl-tab-content name="app-settings">
              <form class="app-form" onsubmit="return false">
                <div class="app-form-row">
                  <sl-label>Project name</sl-label>
                  <sl-input value="Solanda" placeholder="Project name"></sl-input>
                </div>
                <div class="app-form-row">
                  <sl-label>Custom domain</sl-label>
                  <sl-input-group>
                    <sl-input-group-addon slot="prepend">https://</sl-input-group-addon>
                    <sl-input placeholder="solanda.dev"></sl-input>
                    <sl-input-group-addon slot="append">.app</sl-input-group-addon>
                  </sl-input-group>
                </div>
                <div class="app-form-row">
                  <sl-label>Deployment region</sl-label>
                  <sl-select
                    placeholder="Select a region..."
                    value="eu-west"
                    options='[{"value":"eu-west","label":"🇪🇺 Europe West (Frankfurt)"},{"value":"eu-south","label":"🇪🇺 Europe South (Madrid)"},{"value":"sa-east","label":"🇪🇨 South America (Quito)"}]'
                  ></sl-select>
                </div>
                <div class="app-form-row">
                  <sl-label>Description</sl-label>
                  <sl-textarea rows="3" placeholder="What is this project about?" helper-text="Visible in the team dashboard."></sl-textarea>
                </div>
                <div style="display:flex;align-items:center;gap:1.5rem;flex-wrap:wrap">
                  <sl-checkbox label="Main branch protection" checked></sl-checkbox>
                  <div style="display:flex;align-items:center;gap:0.5rem">
                    <sl-label>Email alerts</sl-label>
                    <sl-switch checked></sl-switch>
                  </div>
                </div>
                <div style="display:flex;gap:0.5rem">
                  <sl-button @click="appToast('success', 'Settings saved successfully')">Save changes</sl-button>
                  <sl-button variant="ghost">Discard</sl-button>
                </div>
                <div class="app-danger">
                  <div>
                    <strong style="font-size:var(--sl-font-size-sm)">Danger zone</strong>
                    <p class="text-muted" style="margin:0;font-size:var(--sl-font-size-xs)">Deleting the project is irreversible.</p>
                  </div>
                  <sl-button variant="destructive" size="sm" @click="showDialog">Delete project</sl-button>
                </div>
              </form>
            </sl-tab-content>
          </main>
        </div>
      </div>
    </div>
  </section>

  <!-- Marquee -->
  <div class="marquee-section" aria-hidden="true">
    <div class="marquee-label">65 custom elements · 52 components · tree-shakeable · SSR-ready</div>
    <div class="marquee-track">
      <div class="marquee marquee--left" id="marquee-1"></div>
    </div>
    <div class="marquee-track">
      <div class="marquee marquee--right" id="marquee-2"></div>
    </div>
  </div>

  <!-- Themes -->
  <section id="themes">
    <div class="container">
      <sl-section-header eyebrow="Themes">
        <span slot="title">Identity comes built-in</span>
        Six palettes with their own history, in light and dark. Or replace the tokens and the library adopts your brand without resistance.
      </sl-section-header>

      <div class="theme-switcher">
        <sl-button-group type="radio" value="dark" id="mode-group">
          <sl-button variant="outline" size="sm" value="light">Sun Light</sl-button>
          <sl-button variant="outline" size="sm" value="dark">Moon Dark</sl-button>
        </sl-button-group>
      </div>

      <div class="theme-cards reveal" id="theme-cards"></div>

      <div class="theme-live reveal">
        <div class="theme-live-row">
          <sl-button>Primary</sl-button>
          <sl-button variant="secondary">Secondary</sl-button>
          <sl-button variant="outline">Outline</sl-button>
          <sl-button variant="ghost">Ghost</sl-button>
          <sl-button variant="destructive">Destructive</sl-button>
        </div>
        <div class="theme-live-row">
          <sl-badge variant="default">Default</sl-badge>
          <sl-badge variant="success">Success</sl-badge>
          <sl-badge variant="error">Error</sl-badge>
          <sl-badge variant="warning">Warning</sl-badge>
          <sl-badge variant="info">Info</sl-badge>
          <sl-spinner size="sm"></sl-spinner>
        </div>
        <div class="theme-live-row">
          <sl-input placeholder="Text field..." style="flex:1;min-width:200px"></sl-input>
          <sl-select
            placeholder="Select..."
            options='[{"value":"a","label":"Option A"},{"value":"b","label":"Option B"}]'
            style="flex:1;min-width:200px"
          ></sl-select>
        </div>
        <div class="theme-live-row">
          <sl-checkbox label="Checked option" checked></sl-checkbox>
          <sl-checkbox label="Another option"></sl-checkbox>
          <sl-label>Switch</sl-label>
          <sl-switch checked></sl-switch>
        </div>
      </div>
    </div>
  </section>

  <!-- Get Started -->
  <section id="get-started" class="features">
    <div class="container-wide">
      <sl-section-header eyebrow="Get Started">
        <span slot="title">Three steps. Zero configuration.</span>
        No compilers, no plugins, no peer dependencies. Install, import, and write HTML.
      </sl-section-header>

      <div class="steps-grid">
        <div class="step-card reveal">
          <div class="step-num">1</div>
          <h3 class="step-title">Install</h3>
          <pre class="code-block"><code><span class="c-com"># npm, pnpm, or yarn</span>
npm install @federa/solanda-ui</code></pre>
          <sl-button size="sm" variant="ghost" @click="copyText(installCmd)">⧉ Copy</sl-button>
        </div>
        <div class="step-card reveal" style="--reveal-delay:0.1s">
          <div class="step-num">2</div>
          <h3 class="step-title">Import</h3>
          <pre class="code-block"><code><span class="c-com">// styles (tokens + theme)</span>
<span class="c-kw">import</span> <span class="c-str">'@federa/solanda-ui/css'</span>

<span class="c-com">// everything, or component by component</span>
<span class="c-kw">import</span> <span class="c-str">'@federa/solanda-ui'</span>
<span class="c-kw">import</span> <span class="c-str">'@federa/solanda-ui/sl-dialog'</span></code></pre>
          <sl-button size="sm" variant="ghost" @click="copyText(importSnippet)">⧉ Copy</sl-button>
        </div>
        <div class="step-card reveal" style="--reveal-delay:0.2s">
          <div class="step-num">3</div>
          <h3 class="step-title">Use it</h3>
          <pre class="code-block"><code><span class="c-tag">&lt;sl-dialog</span> <span class="c-attr">id</span>=<span class="c-str">"hello"</span> <span class="c-attr">size</span>=<span class="c-str">"sm"</span><span class="c-tag">&gt;</span>
  <span class="c-tag">&lt;h2</span> <span class="c-attr">slot</span>=<span class="c-str">"title"</span><span class="c-tag">&gt;</span>Hello 👋<span class="c-tag">&lt;/h2&gt;</span>
  This already works.
<span class="c-tag">&lt;/sl-dialog&gt;</span></code></pre>
          <sl-button size="sm" @click="showHello">Try it now</sl-button>
        </div>
      </div>
    </div>
  </section>

  <!-- CTA -->
  <section class="cta">
    <div class="container">
      <div class="cta-box reveal">
        <h2 class="cta-title">Start with the plan.<br>The rest comes naturally.</h2>
        <p class="cta-desc">
          Reference documentation with live demos, code examples, and attribute tables for each component.
        </p>
        <div class="cta-actions">
          <sl-button size="lg" @click="goTo('docs')">Go to documentation</sl-button>
          <sl-button size="lg" variant="outline" @click="openExternal(GITLAB_URL)">View on GitLab</sl-button>
        </div>
      </div>
    </div>
  </section>

  <!-- Footer (sl-footer) -->
  <sl-footer variant="minimal">
    <div slot="start" class="footer-brand">
      <svg class="brand-mark" viewBox="0 0 120 120" aria-hidden="true" focusable="false"><use href="#solanda-mark"/></svg>
      <span>SolandaUI</span>
      <span class="footer-copy">— AGPL-3.0 · Made with its own components</span>
    </div>
    <nav slot="end" class="footer-nav">
      <a href="/docs/">Documentation</a>
      <a href="#features">Features</a>
      <a href="#themes">Themes</a>
      <a href="https://gitlab.com/federa-social/libraries/solanda-ui" target="_blank" rel="noopener">GitLab</a>
      <a href="https://www.npmjs.com/package/@federa/solanda-ui" target="_blank" rel="noopener">npm</a>
    </nav>
  </sl-footer>

  <!-- Global overlays -->
  <sl-offcanvas id="app-offcanvas" ref="appOffcanvas" placement="end" size="sm" backdrop>
    <span slot="title">Notifications</span>
    <sl-list-group variant="flush">
      <sl-list-group-item>
        <div class="app-list-row">
          <span>Deploy #214 completed</span>
          <sl-badge variant="success" size="sm">new</sl-badge>
        </div>
      </sl-list-group-item>
      <sl-list-group-item>
        <div class="app-list-row">
          <span>Maria commented on your MR</span>
          <sl-badge variant="info" size="sm">new</sl-badge>
        </div>
      </sl-list-group-item>
      <sl-list-group-item>May invoice available</sl-list-group-item>
    </sl-list-group>
    <div slot="footer">
      <sl-button size="sm" variant="outline" @click="hideOffcanvas">Close</sl-button>
    </div>
  </sl-offcanvas>

  <sl-dialog id="app-dialog" ref="appDialog" size="sm">
    <h2 slot="title">Delete project?</h2>
    <p>Deployments, domains, and configuration for <strong>Solanda</strong> will be deleted. This action cannot be undone.</p>
    <div slot="footer">
      <sl-button variant="ghost" @click="closeDialog">Cancel</sl-button>
      <sl-button variant="destructive" @click="confirmDelete">Yes, delete</sl-button>
    </div>
  </sl-dialog>

  <sl-dialog id="hello-dialog" ref="helloDialog" size="sm">
    <h2 slot="title">Hello 👋</h2>
    <p>This is a real <code>&lt;sl-dialog&gt;</code>: focus trap, <kbd>Esc</kbd> close, and backdrop included.</p>
    <div slot="footer">
      <sl-button @click="closeHello">Got it</sl-button>
    </div>
  </sl-dialog>

</template>

<style>
/* -- Logo palette: follows active theme -- */
:root {
  --logo-ink:    hsl(var(--sl-hsl-foreground, 0 0% 10%));
  --logo-paper:  hsl(var(--sl-hsl-background, 0 0% 100%));
  --logo-accent: hsl(var(--sl-hsl-primary, 0 0% 55%));
  --logo-muted:  hsl(var(--sl-hsl-muted, 0 0% 70%));
  --logo-soft:   hsl(var(--sl-hsl-secondary, 0 0% 88%));
  --logo-half:   hsl(var(--sl-hsl-muted-foreground, 0 0% 45%));
}

html {
  background: hsl(var(--sl-hsl-background));
  color: hsl(var(--sl-hsl-foreground));
  font-family: var(--sl-font-family-base);
  font-size: var(--sl-font-size-sm);
  scroll-behavior: smooth;
  scroll-padding-top: 4.5rem;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
  overflow-x: hidden;
}

body {
  background: hsl(var(--sl-hsl-background));
  color: hsl(var(--sl-hsl-foreground));
  line-height: 1.6;
  transition: background-color 0.35s ease, color 0.35s ease;
  overflow-x: hidden;
  -webkit-tap-highlight-color: transparent;
}

h1, h2, h3, h4 { font-family: var(--sl-font-family-heading); }

/* -- Utilities -- */
.container {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 2rem;
}

.container-wide {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem;
}

.text-muted { color: hsl(var(--sl-hsl-muted-foreground)); }

section {
  padding: 5.5rem 0;
  scroll-margin-top: 4.5rem;
}

/* -- Nav (sl-topbar) -- */
.landing-nav { position: relative; z-index: 100; max-width: 100vw; overflow: hidden; }

.logo-dot {
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 2px;
  background: hsl(var(--sl-hsl-primary));
  box-shadow: 0 0 0 3px hsl(var(--sl-hsl-primary) / 0.25);
  display: inline-block;
  transform: rotate(45deg);
}

.brand-mark {
  width: 1.75rem;
  height: 1.75rem;
  flex: none;
  transition: transform 0.2s var(--sl-transition-timing-out);
}

.brand-link:hover .brand-mark { transform: translate(-1px, -1px); }

.brand-link {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: var(--sl-font-size-base);
  font-weight: var(--sl-font-weight-semibold);
  color: hsl(var(--sl-hsl-foreground));
  text-decoration: none;
  letter-spacing: -0.01em;
}

.topbar-links {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.topbar-links a {
  padding: 0.375rem 0.75rem;
  font-size: var(--sl-font-size-sm);
  color: hsl(var(--sl-hsl-muted-foreground));
  text-decoration: none;
  border-radius: var(--sl-radius-md);
  transition: color 0.15s, background-color 0.15s;
}

.topbar-links a:hover {
  color: hsl(var(--sl-hsl-foreground));
  background: hsl(var(--sl-hsl-muted));
}

.topbar-links a.active {
  color: hsl(var(--sl-hsl-primary));
  background: hsl(var(--sl-hsl-primary) / 0.1);
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 760px) {
  .topbar-links { display: none; }
}

/* Narrow phones: keep the topbar from overflowing by trimming the side actions */
@media (max-width: 480px) {
  .topbar-actions { gap: 0.25rem; min-width: 0; flex-shrink: 1; }
  .topbar-actions .lang-selector { display: none !important; }
  .brand-link { gap: 0.4rem; min-width: 0; }
}

@media (max-width: 360px) {
  /* On very small screens drop the secondary GitLab link; theme toggle stays */
  .topbar-actions > sl-button:last-of-type { display: none; }
}

/* -- Hero -- */
.hero {
  padding-top: 8rem;
  padding-bottom: 6rem;
  position: relative;
  overflow: hidden;
}

/* Urban grid: the neighborhood plan as background */
.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(hsl(var(--sl-hsl-border) / 0.5) 1px, transparent 1px),
    linear-gradient(90deg, hsl(var(--sl-hsl-border) / 0.5) 1px, transparent 1px),
    linear-gradient(hsl(var(--sl-hsl-border) / 0.25) 1px, transparent 1px),
    linear-gradient(90deg, hsl(var(--sl-hsl-border) / 0.25) 1px, transparent 1px);
  background-size: 168px 168px, 168px 168px, 28px 28px, 28px 28px;
  mask-image: radial-gradient(ellipse 90% 80% at 70% 10%, black 20%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse 90% 80% at 70% 10%, black 20%, transparent 75%);
  pointer-events: none;
}

.hero::after {
  content: '';
  position: absolute;
  top: -25%;
  right: -10%;
  width: 720px;
  height: 720px;
  background:
    radial-gradient(circle at 60% 40%, hsl(var(--sl-hsl-primary) / 0.16) 0%, transparent 55%),
    radial-gradient(circle at 30% 70%, hsl(var(--sl-hsl-secondary, var(--sl-hsl-primary)) / 0.35) 0%, transparent 55%);
  pointer-events: none;
}

.hero .container-wide {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 4rem;
  align-items: center;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3rem 0.875rem;
  border-radius: var(--sl-radius-full);
  border: 1px solid hsl(var(--sl-hsl-primary) / 0.4);
  background: hsl(var(--sl-hsl-primary) / 0.08);
  color: hsl(var(--sl-hsl-primary));
  font-size: var(--sl-font-size-xs);
  font-weight: var(--sl-font-weight-medium);
  margin-bottom: 1.75rem;
}

.hero-badge-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: hsl(var(--sl-hsl-primary));
  animation: pulse-dot 2.5s ease infinite;
}

@keyframes pulse-dot {
  0%, 100% { box-shadow: 0 0 0 0 hsl(var(--sl-hsl-primary) / 0.5); }
  50% { box-shadow: 0 0 0 5px hsl(var(--sl-hsl-primary) / 0); }
}

.hero-title {
  font-size: clamp(2.5rem, 5.5vw, 3.875rem);
  font-weight: var(--sl-font-weight-bold);
  line-height: 1.06;
  letter-spacing: -0.025em;
  margin: 0 0 1.25rem;
}

.hero-title em {
  font-style: normal;
  color: hsl(var(--sl-hsl-primary));
  position: relative;
  white-space: nowrap;
}

/* Hard underline block, echo of logo shadow */
.hero-title em::after {
  content: '';
  position: absolute;
  left: 0.06em;
  right: -0.06em;
  bottom: -0.02em;
  height: 0.14em;
  background: hsl(var(--sl-hsl-primary) / 0.35);
  border-radius: 0;
}

/* Hero large logo */
.hero-logo {
  display: block;
  width: min(400px, 100%);
  height: auto;
  margin-bottom: 2rem;
}

.hero-subtitle {
  font-size: clamp(1rem, 1.6vw, 1.125rem);
  color: hsl(var(--sl-hsl-muted-foreground));
  max-width: 52ch;
  margin: 0 0 2.25rem;
  line-height: 1.7;
}

.hero-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.hero-install {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.625rem 0.5rem 1.25rem;
  border: 2px solid hsl(var(--sl-hsl-foreground) / 0.75);
  border-radius: var(--sl-radius-lg);
  background: hsl(var(--sl-hsl-muted) / 0.5);
  font-family: var(--sl-font-family-mono);
  font-size: var(--sl-font-size-sm);
  margin-bottom: 2.75rem;
  box-shadow: 4px 4px 0 hsl(var(--sl-hsl-foreground) / 0.2);
}

.hero-install code::before {
  content: '$ ';
  color: hsl(var(--sl-hsl-primary));
}

.hero-stats {
  display: flex;
  gap: 2.5rem;
  flex-wrap: wrap;
  padding-top: 1.75rem;
  border-top: 1px solid hsl(var(--sl-hsl-border) / 0.7);
}

.hero-stat-value {
  font-size: 1.875rem;
  font-weight: var(--sl-font-weight-semibold);
  line-height: 1;
  margin-bottom: 0.3rem;
  font-variant-numeric: tabular-nums;
}

.hero-stat-label {
  font-size: var(--sl-font-size-xs);
  color: hsl(var(--sl-hsl-muted-foreground));
}

/* -- Hero: floating app -- */
.hero-visual {
  position: relative;
  min-height: 460px;
}

.hero-app {
  position: relative;
  border: 2px solid hsl(var(--sl-hsl-foreground) / 0.8);
  border-radius: var(--sl-radius-xl);
  background: hsl(var(--sl-hsl-card));
  box-shadow: 12px 12px 0 hsl(var(--sl-hsl-foreground) / 0.16), 0 0 80px hsl(var(--sl-hsl-primary) / 0.08);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  transform: perspective(1400px) rotateY(-7deg) rotateX(2deg);
  transition: transform 0.5s var(--sl-transition-timing-out);
}

.hero-visual:hover .hero-app {
  transform: perspective(1400px) rotateY(-2deg) rotateX(0.5deg);
}

.hero-app-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.hero-app-title {
  font-weight: var(--sl-font-weight-semibold);
  font-size: var(--sl-font-size-base);
}

.hero-app-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid hsl(var(--sl-hsl-border) / 0.7);
  border-radius: var(--sl-radius-lg);
  background: hsl(var(--sl-hsl-background) / 0.5);
}

.hero-app-row-label {
  font-size: var(--sl-font-size-sm);
  display: flex;
  flex-direction: column;
}

.hero-app-row-label small {
  color: hsl(var(--sl-hsl-muted-foreground));
  font-size: var(--sl-font-size-xs);
}

.avatar-stack { display: flex; }
.avatar-stack sl-avatar:not(:first-child) { margin-left: -0.55rem; }

.hero-float {
  position: absolute;
  border: 2px solid hsl(var(--sl-hsl-foreground) / 0.7);
  border-radius: var(--sl-radius-lg);
  background: hsl(var(--sl-hsl-card));
  box-shadow: 4px 4px 0 hsl(var(--sl-hsl-foreground) / 0.2);
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: var(--sl-font-size-xs);
  z-index: 2;
}

.hero-float-a {
  top: -1.5rem;
  right: 0.5rem;
  animation: float-a 7s ease-in-out infinite;
}

.hero-float-b {
  bottom: -1.25rem;
  left: -1.5rem;
  animation: float-b 8s ease-in-out infinite;
}

@keyframes float-a {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@keyframes float-b {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(9px); }
}

@media (max-width: 980px) {
  .hero .container-wide { grid-template-columns: 1fr; gap: 4.5rem; }
  .hero-visual { min-height: 0; }
  .hero-app { transform: none; }
  .hero-float-a { top: -1rem; right: 0; }
  .hero-float-b { bottom: -1rem; left: 0; }
}

/* -- Bento grid -- */
.features {
  background: hsl(var(--sl-hsl-card) / 0.45);
  border-top: 1px solid hsl(var(--sl-hsl-border));
  border-bottom: 1px solid hsl(var(--sl-hsl-border));
}

.bento {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-top: 3rem;
}

.bento-cell {
  border: 1px solid hsl(var(--sl-hsl-border));
  border-radius: var(--sl-radius-xl);
  background: hsl(var(--sl-hsl-background));
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
  transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s;
}

.bento-cell:hover {
  border-color: hsl(var(--sl-hsl-primary) / 0.45);
  box-shadow: 0 8px 32px hsl(var(--sl-hsl-primary) / 0.07);
  transform: translateY(-2px);
}

.bento-2 { grid-column: span 2; }
.bento-tall { grid-row: span 2; }

.bento-eyebrow {
  font-size: var(--sl-font-size-xs);
  font-weight: var(--sl-font-weight-medium);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: hsl(var(--sl-hsl-primary));
  margin-bottom: 0.5rem;
}

.bento-title {
  font-size: var(--sl-font-size-lg);
  font-weight: var(--sl-font-weight-semibold);
  margin: 0 0 0.5rem;
  letter-spacing: -0.01em;
}

.bento-desc {
  font-size: var(--sl-font-size-sm);
  color: hsl(var(--sl-hsl-muted-foreground));
  margin: 0 0 1.25rem;
  line-height: 1.65;
}

.bento-desc:last-child { margin-bottom: 0; }

@media (max-width: 980px) {
  .bento { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 640px) {
  .bento { grid-template-columns: 1fr; }
  .bento-2 { grid-column: span 1; }
}

/* Code */
.code-block {
  background: hsl(var(--sl-hsl-muted) / 0.5);
  border: 1px solid hsl(var(--sl-hsl-border) / 0.8);
  border-radius: var(--sl-radius-lg);
  padding: 1rem 1.25rem;
  overflow-x: auto;
  margin: 0;
}

.code-block code {
  font-family: var(--sl-font-family-mono);
  font-size: 0.8125rem;
  line-height: 1.7;
  white-space: pre;
  display: block;
}

.c-tag { color: hsl(var(--sl-hsl-primary)); }
.c-attr { color: hsl(var(--sl-hsl-secondary-foreground, var(--sl-hsl-info))); }
.c-str { color: hsl(var(--sl-hsl-success)); }
.c-kw { color: hsl(var(--sl-hsl-info)); }
.c-com { color: hsl(var(--sl-hsl-muted-foreground)); font-style: italic; }

/* Weight bars */
.weight-bars { display: flex; flex-direction: column; gap: 0.875rem; }

.weight-row { display: flex; flex-direction: column; gap: 0.3rem; }

.weight-row-head {
  display: flex;
  justify-content: space-between;
  font-size: var(--sl-font-size-xs);
}

.weight-row-head strong { font-weight: var(--sl-font-weight-medium); }
.weight-row-head span { color: hsl(var(--sl-hsl-muted-foreground)); font-variant-numeric: tabular-nums; }

.weight-track {
  height: 0.5rem;
  border-radius: var(--sl-radius-full);
  background: hsl(var(--sl-hsl-muted));
  overflow: hidden;
}

.weight-fill {
  height: 100%;
  width: 0;
  border-radius: var(--sl-radius-full);
  background: hsl(var(--sl-hsl-primary));
  transition: width 1.1s var(--sl-transition-timing-out) 0.2s;
}

.weight-fill--muted { background: hsl(var(--sl-hsl-muted-foreground) / 0.45); }

.is-visible .weight-fill { width: var(--w); }

/* Theme swatches in bento */
.bento-themes-list { display: flex; flex-direction: column; gap: 0.5rem; }

.bento-theme-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid hsl(var(--sl-hsl-border) / 0.8);
  border-radius: var(--sl-radius-md);
  background: transparent;
  color: hsl(var(--sl-hsl-foreground));
  font-family: inherit;
  font-size: var(--sl-font-size-sm);
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
}

.bento-theme-btn:hover { background: hsl(var(--sl-hsl-muted) / 0.6); }

.bento-theme-btn[aria-pressed='true'] {
  border-color: hsl(var(--sl-hsl-primary));
  background: hsl(var(--sl-hsl-primary) / 0.08);
}

.swatch-row { display: flex; gap: 0.25rem; }

.swatch {
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 50%;
  border: 1px solid hsl(var(--sl-hsl-border));
  display: inline-block;
}

/* Tokens demo */
.token-chips { display: flex; flex-wrap: wrap; gap: 0.375rem; }

.token-chip {
  font-family: var(--sl-font-family-mono);
  font-size: 0.6875rem;
  padding: 0.25rem 0.5rem;
  border-radius: var(--sl-radius-sm);
  background: hsl(var(--sl-hsl-muted) / 0.7);
  color: hsl(var(--sl-hsl-muted-foreground));
  border: 1px solid hsl(var(--sl-hsl-border) / 0.6);
}

.token-chip b { color: hsl(var(--sl-hsl-primary)); font-weight: var(--sl-font-weight-medium); }

/* -- Demo app -- */
.app-window {
  border: 1px solid hsl(var(--sl-hsl-border));
  border-radius: var(--sl-radius-xl);
  overflow: hidden;
  box-shadow: var(--sl-shadow-2xl);
  background: hsl(var(--sl-hsl-background));
  margin-top: 3rem;
}

.app-window-bar {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  height: 2.75rem;
  padding: 0 1rem;
  background: hsl(var(--sl-hsl-card));
  border-bottom: 1px solid hsl(var(--sl-hsl-border));
}

.window-dot { width: 0.625rem; height: 0.625rem; border-radius: 50%; }

.window-url {
  flex: 1;
  max-width: 320px;
  margin: 0 auto;
  text-align: center;
  font-size: var(--sl-font-size-xs);
  font-family: var(--sl-font-family-mono);
  color: hsl(var(--sl-hsl-muted-foreground));
  background: hsl(var(--sl-hsl-background));
  border: 1px solid hsl(var(--sl-hsl-border) / 0.7);
  border-radius: var(--sl-radius-md);
  padding: 0.2rem 0.75rem;
}

.app-body {
  display: flex;
  align-items: stretch;
  min-height: 560px;
}

.app-body sl-sidebar { flex-shrink: 0; }

.app-nav-section {
  font-size: var(--sl-font-size-xs);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: hsl(var(--sl-hsl-muted-foreground));
  padding: 1rem 1rem 0.5rem;
}

.app-main {
  flex: 1;
  min-width: 0;
  padding: 1.5rem 2rem 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.app-page-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.app-page-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: var(--sl-font-size-xl);
  font-weight: var(--sl-font-weight-semibold);
  margin: 0;
  letter-spacing: -0.01em;
}

.app-actions { display: flex; gap: 0.5rem; align-items: center; }

.app-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.app-stat-label {
  font-size: var(--sl-font-size-xs);
  color: hsl(var(--sl-hsl-muted-foreground));
  margin-bottom: 0.25rem;
}

.app-stat-value {
  font-size: var(--sl-font-size-2xl);
  font-weight: var(--sl-font-weight-semibold);
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
}

.app-list-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.app-list-main { display: flex; align-items: center; gap: 0.75rem; min-width: 0; }

.app-list-text { display: flex; flex-direction: column; min-width: 0; }

.app-list-text small {
  color: hsl(var(--sl-hsl-muted-foreground));
  font-size: var(--sl-font-size-xs);
}

.app-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 520px;
}

.app-form-row { display: flex; flex-direction: column; gap: 0.375rem; }

.app-danger {
  margin-top: 0.5rem;
  padding: 1rem 1.25rem;
  border: 1px solid hsl(var(--sl-hsl-destructive) / 0.4);
  border-radius: var(--sl-radius-lg);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.demo-pagination-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

@media (max-width: 900px) {
  .app-body sl-sidebar { display: none; }
  .app-main { padding: 1.25rem 1.25rem 2rem; }
  .app-stats { grid-template-columns: 1fr; }
}

/* -- Marquee -- */
.marquee-section {
  padding: 3.5rem 0;
  border-top: 1px solid hsl(var(--sl-hsl-border));
  border-bottom: 1px solid hsl(var(--sl-hsl-border));
  background: hsl(var(--sl-hsl-card) / 0.45);
  overflow: hidden;
}

.marquee-label {
  text-align: center;
  font-size: var(--sl-font-size-xs);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: hsl(var(--sl-hsl-muted-foreground));
  margin-bottom: 1.75rem;
}

.marquee {
  display: flex;
  gap: 0.625rem;
  width: max-content;
  margin-bottom: 0.75rem;
}

.marquee-track { overflow: hidden; position: relative; }

.marquee-track::before,
.marquee-track::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 8rem;
  z-index: 1;
  pointer-events: none;
}

.marquee-track::before {
  left: 0;
  background: linear-gradient(90deg, hsl(var(--sl-hsl-background)) 0%, transparent 100%);
}

.marquee-track::after {
  right: 0;
  background: linear-gradient(-90deg, hsl(var(--sl-hsl-background)) 0%, transparent 100%);
}

.marquee-chip {
  font-family: var(--sl-font-family-mono);
  font-size: var(--sl-font-size-xs);
  padding: 0.4rem 0.875rem;
  border: 1px solid hsl(var(--sl-hsl-border));
  border-radius: var(--sl-radius-full);
  background: hsl(var(--sl-hsl-background));
  color: hsl(var(--sl-hsl-muted-foreground));
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;
}

.marquee-chip:hover {
  color: hsl(var(--sl-hsl-primary));
  border-color: hsl(var(--sl-hsl-primary) / 0.5);
}

@media (prefers-reduced-motion: no-preference) {
  .marquee--left { animation: marquee-l 48s linear infinite; }
  .marquee--right { animation: marquee-r 56s linear infinite; }
  .marquee-track:hover .marquee { animation-play-state: paused; }
}

@keyframes marquee-l {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}

@keyframes marquee-r {
  from { transform: translateX(-50%); }
  to { transform: translateX(0); }
}

/* -- Themes -- */
.theme-switcher {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
  margin: 2.5rem 0;
}

.theme-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 1rem;
}

.theme-card {
  border: 1px solid hsl(var(--sl-hsl-border));
  border-radius: var(--sl-radius-lg);
  background: hsl(var(--sl-hsl-background));
  padding: 1.125rem 1.25rem;
  text-align: left;
  font-family: inherit;
  color: hsl(var(--sl-hsl-foreground));
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}

.theme-card:hover { transform: translateY(-2px); box-shadow: var(--sl-shadow-md); }

.theme-card[aria-pressed='true'] {
  border-color: hsl(var(--sl-hsl-primary));
  box-shadow: 0 0 0 1px hsl(var(--sl-hsl-primary)), 0 6px 24px hsl(var(--sl-hsl-primary) / 0.12);
}

.theme-card-name {
  font-weight: var(--sl-font-weight-semibold);
  font-size: var(--sl-font-size-sm);
  margin-bottom: 0.2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.theme-card-desc {
  font-size: var(--sl-font-size-xs);
  color: hsl(var(--sl-hsl-muted-foreground));
  margin-bottom: 0.875rem;
  line-height: 1.5;
}

.theme-live {
  margin-top: 2.5rem;
  border: 1px solid hsl(var(--sl-hsl-border));
  border-radius: var(--sl-radius-xl);
  background: hsl(var(--sl-hsl-card) / 0.5);
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.125rem;
}

.theme-live-row {
  display: flex;
  gap: 0.625rem;
  align-items: center;
  flex-wrap: wrap;
}

/* -- Get Started -- */
.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  margin-top: 3rem;
}

.step-card {
  border: 1px solid hsl(var(--sl-hsl-border));
  border-radius: var(--sl-radius-xl);
  background: hsl(var(--sl-hsl-card) / 0.5);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.step-num {
  width: 2rem;
  height: 2rem;
  border-radius: var(--sl-radius-md);
  background: hsl(var(--sl-hsl-primary) / 0.12);
  color: hsl(var(--sl-hsl-primary));
  border: 1px solid hsl(var(--sl-hsl-primary) / 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--sl-font-weight-semibold);
  font-size: var(--sl-font-size-sm);
}

.step-title {
  font-size: var(--sl-font-size-base);
  font-weight: var(--sl-font-weight-semibold);
  margin: 0;
}

.step-card .code-block { flex: 1; }

@media (max-width: 900px) {
  .steps-grid { grid-template-columns: 1fr; }
}

/* -- CTA -- */
.cta { text-align: center; }

.cta-box {
  border-radius: var(--sl-radius-2xl);
  border: 1px solid hsl(var(--sl-hsl-border));
  background: hsl(var(--sl-hsl-card) / 0.6);
  padding: 4.5rem 2rem;
  position: relative;
  overflow: hidden;
}

.cta-box::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(ellipse at 50% 0%, hsl(var(--sl-hsl-primary) / 0.13) 0%, transparent 55%),
    linear-gradient(hsl(var(--sl-hsl-border) / 0.4) 1px, transparent 1px),
    linear-gradient(90deg, hsl(var(--sl-hsl-border) / 0.4) 1px, transparent 1px);
  background-size: auto, 42px 42px, 42px 42px;
  mask-image: radial-gradient(ellipse 80% 90% at 50% 0%, black 20%, transparent 80%);
  -webkit-mask-image: radial-gradient(ellipse 80% 90% at 50% 0%, black 20%, transparent 80%);
  pointer-events: none;
}

.cta-box > * { position: relative; }

.cta-title {
  font-size: clamp(1.625rem, 3.2vw, 2.5rem);
  font-weight: var(--sl-font-weight-bold);
  letter-spacing: -0.02em;
  margin: 0 0 0.875rem;
}

.cta-desc {
  color: hsl(var(--sl-hsl-muted-foreground));
  margin: 0 auto 2.25rem;
  max-width: 44ch;
  line-height: 1.7;
}

.cta-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
}

/* -- Footer -- */
.footer-brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: var(--sl-font-weight-medium);
}

.footer-copy {
  font-size: var(--sl-font-size-xs);
  color: hsl(var(--sl-hsl-muted-foreground));
}

.footer-nav { display: flex; gap: 1.25rem; flex-wrap: wrap; }

.footer-nav a {
  font-size: var(--sl-font-size-xs);
  color: hsl(var(--sl-hsl-muted-foreground));
  text-decoration: none;
  transition: color 0.15s;
}

.footer-nav a:hover { color: hsl(var(--sl-hsl-foreground)); }

/* -- Scroll reveal -- */
@media (prefers-reduced-motion: no-preference) {
  .reveal {
    opacity: 0;
    transform: translateY(18px);
    transition: opacity 0.65s ease, transform 0.65s ease;
    transition-delay: var(--reveal-delay, 0s);
  }
  .reveal.is-visible { opacity: 1; transform: none; }
}
</style>
